import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { ControlContainer, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Directive({
  exportAs: 'validityState',
  selector: '[portalInputValidityState]',
  standalone: false,
})
export class InputValidityStateDirective implements OnInit {
  // Can be used if the formControlName directive cannot be used.
  @Input()
  controlName?: string;

  @Input()
  formControlName: string;

  /**
   * Errors on the parent control that should cause this form control to be displayed as invalid.
   */
  @Input()
  formErrors: string[] = [];

  @Input()
  highlightIfFormGroupIsInvalid: boolean;

  @Input()
  noIsValid: boolean = false;

  @Input()
  skipValidationIfPristine: boolean;

  constructor(
    private el: ElementRef,
    private form: ControlContainer,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (this.highlightIfFormGroupIsInvalid !== undefined) {
      this.highlightIfFormGroupIsInvalid = true;
    }

    if (this.skipValidationIfPristine !== undefined) {
      this.skipValidationIfPristine = true;
    }

    this.setValidity();

    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.setValidity();
    });

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.setValidity();
    });
  }

  @HostListener('input')
  onInput() {
    this.setValidity();
  }

  @HostListener('blur')
  onBlur() {
    this.setValidity();
  }

  /**
   * Update the validation classes.
   *
   * Normally this should not be necessary because we listen to the status and
   * value changes directly. But when using an async validator it can happen
   * that the control remains in the pending state.
   */
  update(): void {
    this.setValidity();
  }

  private setValidity() {
    const form: UntypedFormGroup = this.form.control as UntypedFormGroup;
    const control = form.get(this.formControlName ?? this.controlName);

    // Can be null if a formGroup in a form with multiple form groups (i.e. probably a form with a FormArray)
    // has been removed and therefore the value of the form has changed.
    // If the control is pending, nothing will be changed.
    if (control !== null && control.pending === false) {
      if (control.enabled) {
        const controlIsInvalid = control.invalid || this.formIsInvalid();
        const skipValidation = control.pristine === true && this.skipValidationIfPristine === true;

        if (controlIsInvalid) {
          if (skipValidation === false) {
            // Always show that the control is invalid if it's invalid (but not if the validation should be skipped if it's pristine)
            this.markInvalid();
          }
        } else if (control.pristine) {
          // Show nothing if the control is valid but is not dirty
          this.removeStyles();
        } else {
          // Show that the control is valid if it has been touched and is valid
          this.markValid();
        }
      } else {
        // Don't show any validation style if the element is disabled
        this.removeStyles();
      }
    }
  }

  private formIsInvalid(): boolean {
    let invalid = false;

    if (this.form.errors != null) {
      if (this.highlightIfFormGroupIsInvalid) {
        invalid = true;
      } else if (this.formErrors.length > 0) {
        invalid = this.formErrors.reduce((invalid, key) => invalid || this.form.errors[key] != null, false);
      }
    }

    return invalid;
  }

  private markInvalid(): void {
    this.renderer.removeClass(this.el.nativeElement, 'is-valid');
    this.renderer.addClass(this.el.nativeElement, 'is-invalid');
  }

  private removeStyles(): void {
    this.renderer.removeClass(this.el.nativeElement, 'is-invalid');

    if (this.noIsValid === false) {
      this.renderer.removeClass(this.el.nativeElement, 'is-valid');
    }
  }

  private markValid(): void {
    this.renderer.removeClass(this.el.nativeElement, 'is-invalid');

    if (this.noIsValid === false) {
      this.renderer.addClass(this.el.nativeElement, 'is-valid');
    }
  }
}
